import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Trash2, Asterisk } from 'lucide-react'
import type { SchemaField } from '../../../types/schede'
import { FIELD_PALETTE } from '../../../types/schede'

interface SortableFieldCardProps {
  field: SchemaField
  isSelected: boolean
  onSelect: () => void
  onRemove: () => void
}

const TYPE_COLORS: Record<string, string> = {
  section: 'var(--color-warning, #f59e0b)',
  label: 'var(--color-text-tertiary, #9ca3af)',
  calculated: 'var(--color-success, #22c55e)',
  signature: 'var(--color-danger, #ef4444)',
  table: 'var(--color-info, #3b82f6)',
}

export default function SortableFieldCard({ field, isSelected, onSelect, onRemove }: SortableFieldCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  const paletteItem = FIELD_PALETTE.find(p => p.type === field.type)
  const typeLabel = paletteItem?.label || field.type
  const accentColor = TYPE_COLORS[field.type] || 'var(--color-primary)'

  const widthLabel = field.width === 'half' ? '50%' : field.width === 'third' ? '33%' : '100%'

  return (
    <div
      ref={setNodeRef}
      onClick={onSelect}
      className={`group flex items-center gap-2 rounded-lg border px-3 py-2.5 cursor-pointer transition-all ${
        isSelected ? 'ring-2' : 'hover:shadow-sm'
      }`}
      data-selected={isSelected}
      style={{
        ...style,
        borderColor: isSelected ? accentColor : 'var(--color-border)',
        backgroundColor: field.type === 'section'
          ? 'var(--color-bg-surface, rgba(99,102,241,0.05))'
          : 'var(--color-bg-card)',
        ...(isSelected ? { boxShadow: `0 0 0 2px ${accentColor}` } : {}),
      }}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5"
        style={{ color: 'var(--color-text-tertiary)' }}
      >
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Type badge */}
      <span
        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
        style={{
          backgroundColor: `${accentColor}20`,
          color: accentColor,
        }}
      >
        {typeLabel}
      </span>

      {/* Label */}
      <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
        {field.label}
        {field.required && (
          <Asterisk className="w-3 h-3 inline ml-1" style={{ color: 'var(--color-danger, #ef4444)' }} />
        )}
      </span>

      {/* Width indicator */}
      <span className="text-[10px] shrink-0" style={{ color: 'var(--color-text-tertiary)' }}>
        {widthLabel}
      </span>

      {/* Delete */}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        className="opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity hover:bg-red-50"
        style={{ color: 'var(--color-danger, #ef4444)' }}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
